import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Stack,
  Chip,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalShipping as LocalShippingIcon,
  Recycling as RecyclingIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';

interface WasteEntry {
  id: string;
  type: string;
  amount: number;
  unit: string;
  date: string;
  status: 'collected' | 'processing' | 'recycled' | 'disposed';
  location: string;
}

const WasteManagement: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const [wasteEntries] = useState<WasteEntry[]>([
    {
      id: '1',
      type: 'plastic',
      amount: 2500,
      unit: 'kg',
      date: '2024-03-15',
      status: 'recycled',
      location: 'Main Facility',
    },
    {
      id: '2',
      type: 'paper',
      amount: 1800,
      unit: 'kg',
      date: '2024-03-14',
      status: 'processing',
      location: 'Recycling Center',
    },
  ]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };


  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    handleCloseDialog();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'recycled':
        return theme.palette.success.main;
      case 'processing':
        return theme.palette.warning.main;
      case 'collected':
        return theme.palette.info.main;
      case 'disposed':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'recycled':
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      case 'processing':
        return <RecyclingIcon sx={{ color: theme.palette.warning.main }} />;
      case 'collected':
        return <LocalShippingIcon sx={{ color: theme.palette.info.main }} />;
      case 'disposed':
        return <WarningIcon sx={{ color: theme.palette.error.main }} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader icon={<DeleteIcon />} title={t('wasteManagement.title')} />

      {/* Waste Analytics Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: '16px' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>{t('wasteManagement.metrics.totalCollected')}</Typography>
              <Typography variant="h3" sx={{ mb: 1 }}>4,300 kg</Typography>
              <Typography variant="body2" color="text.secondary">{t('wasteManagement.subtitles.thisMonth')}</Typography>
              <LinearProgress 
                variant="determinate" 
                value={75} 
                sx={{ 
                  mt: 2,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: `${theme.palette.primary.main}20`,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: theme.palette.primary.main,
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: '16px' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>{t('wasteManagement.metrics.recyclingRate')}</Typography>
              <Typography variant="h3" sx={{ mb: 1 }}>78%</Typography>
              <Typography variant="body2" color="text.secondary">{t('wasteManagement.subtitles.target85')}</Typography>
              <LinearProgress 
                variant="determinate" 
                value={78} 
                sx={{ 
                  mt: 2,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: `${theme.palette.success.main}20`,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: theme.palette.success.main,
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: '16px' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>{t('wasteManagement.metrics.carbonOffset')}</Typography>
              <Typography variant="h3" sx={{ mb: 1 }}>2.5 tons</Typography>
              <Typography variant="body2" color="text.secondary">{t('wasteManagement.subtitles.co2eSaved')}</Typography>
              <LinearProgress 
                variant="determinate" 
                value={65} 
                sx={{ 
                  mt: 2,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: `${theme.palette.info.main}20`,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: theme.palette.info.main,
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Waste Entries List */}
      <Grid container spacing={3}>
        {wasteEntries.map((entry) => (
          <Grid item xs={12} md={6} key={entry.id}>
            <Card sx={{
              borderRadius: '16px',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[4],
              },
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t(`wasteManagement.wasteTypes.${entry.type}`, { defaultValue: entry.type })}
                    </Typography>
                    <Typography variant="h4" sx={{ my: 1 }}>
                      {entry.amount} {entry.unit}
                    </Typography>
                  </Box>
                  <IconButton onClick={(e) => handleMenuOpen(e)}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip
                    icon={getStatusIcon(entry.status) || undefined}
                    label={t(`wasteManagement.status.${entry.status}`)}
                    size="small"
                    sx={{
                      bgcolor: `${getStatusColor(entry.status)}10`,
                      color: getStatusColor(entry.status),
                    }}
                  />
                  <Chip
                    label={entry.location}
                    size="small"
                    sx={{
                      bgcolor: theme.palette.background.default,
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t('wasteManagement.collectedOn', {
                    date: new Date(entry.date).toLocaleDateString(),
                  })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: "12px",
          }
        }}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
          </ListItemIcon>
          <ListItemText>{t('common.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            borderRadius: "16px",
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: "16px 16px 0 0",
        }}>
          {t('wasteManagement.addWasteEntry')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>{t('wasteManagement.columns.wasteType')}</InputLabel>
              <Select
                label={t('wasteManagement.columns.wasteType')}
                defaultValue=""
              >
                <MenuItem value="plastic">{t('wasteManagement.wasteTypes.plastic')}</MenuItem>
                <MenuItem value="paper">{t('wasteManagement.wasteTypes.paper')}</MenuItem>
                <MenuItem value="glass">{t('wasteManagement.wasteTypes.glass')}</MenuItem>
                <MenuItem value="metal">{t('wasteManagement.wasteTypes.metal')}</MenuItem>
                <MenuItem value="organic">{t('wasteManagement.wasteTypes.organic')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('wasteManagement.columns.amount')}
              type="number"
              fullWidth
              variant="outlined"
            />
            <FormControl fullWidth>
              <InputLabel>{t('wasteManagement.columns.unit')}</InputLabel>
              <Select
                label={t('wasteManagement.columns.unit')}
                defaultValue="kg"
              >
                <MenuItem value="kg">{t('wasteManagement.units.kilograms')}</MenuItem>
                <MenuItem value="tons">{t('wasteManagement.units.metricTons')}</MenuItem>
                <MenuItem value="liters">{t('wasteManagement.units.liters')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('wasteManagement.columns.date')}
              type="date"
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel>{t('wasteManagement.columns.status')}</InputLabel>
              <Select
                label={t('wasteManagement.columns.status')}
                defaultValue="collected"
              >
                <MenuItem value="collected">{t('wasteManagement.status.collected')}</MenuItem>
                <MenuItem value="processing">{t('wasteManagement.status.processing')}</MenuItem>
                <MenuItem value="recycled">{t('wasteManagement.status.recycled')}</MenuItem>
                <MenuItem value="disposed">{t('wasteManagement.status.disposed')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('wasteManagement.columns.location')}
              fullWidth
              variant="outlined"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
              borderRadius: '8px',
              px: 3,
            }}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WasteManagement;
